"use client";

import { Box, IconButton, Typography } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import {
  BRAND_COLOR,
  BREAKPOINT_NAV_DESKTOP_MIN,
  BREAKPOINT_NAV_MOBILE_MIN,
  BREAKPOINT_NAV_RELAXED_MIN,
  BREAKPOINT_NAV_WRAPPED_MIN,
  HIDE_SCROLLBAR_STYLE,
  META_AUSTRALIAS_NUMBER_1,
  META_BRAND_NAME,
  PAGE_MAX_WIDTH,
  Z_INDEX_BASE,
  Z_INDEX_MENUS,
} from "./LayoutConstants";
import { useEffect, useRef } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { GlobalMemberMenu } from "@/components/nav/GlobalMemberMenu";
import { GlobalNavMenu } from "@/components/nav/GlobalNavMenu";
import { usePathname } from "next/navigation";
import { useTopNavHeadroom } from "./useTopNavHeadroom";
import Clarity from "@microsoft/clarity";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import { useDeviceNavigation } from "./useDeviceNavigation";
import HorseIcon from "@/../public/icons/horse.png";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HomeIcon from "@mui/icons-material/Home";
import SaddleIcon from "@/../public/icons/saddle.png";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import SellIcon from "@mui/icons-material/Sell";
import TubesIcon from "@/../public/icons/tubes.png";
import { JockeyIcon } from "../shared/icons/JockeyIcon";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CategoryIcon from "@mui/icons-material/Category";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

Clarity.init("rei1n2dyyn");

const TOP_NAV_LOGO_WIDTH_DESKTOP = 270;
const TOP_NAV_LOGO_WIDTH_MOBILE = 216;
export const TOP_NAV_MENU_HEIGHT_LARGE = 64;
const TOP_NAV_MENU_HEIGHT_SMALL = 56;
const TOP_NAV_SUB_MENU_HEIGHT = 57;
export const TOP_NAV_HEIGHT_DESKTOP =
  TOP_NAV_MENU_HEIGHT_LARGE + TOP_NAV_SUB_MENU_HEIGHT;

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  href: string;
  basePath?: string;
  children?: MenuItem[];
  fullRefresh?: boolean;
}

export const MENU_ITEM_ICONS: Record<string, React.ReactNode> = {
  "everything-equine": <CategoryIcon />,
  horses: <Image src={HorseIcon} alt="Horses" width={60} height={60} />,
  "transport-vehicles": <LocalShippingIcon />,
  "saddlery-tack-apparel": (
    <Image
      src={SaddleIcon}
      alt="Saddlery, Tack & Apparel"
      width={60}
      height={60}
    />
  ),
  "equine-property": <HomeIcon />,
  "stallions-at-stud": (
    <Image src={TubesIcon} alt="Stallions at Stud" width={60} height={60} />
  ),
  "products-equipment": <SellIcon />,
  services: <HomeRepairServiceIcon />,
  "off-the-track": <JockeyIcon size="large" />,
};

const MENU_ITEMS: MenuItem[] = [
  {
    label: "Search",
    href: "/for-sale/everything-equine",
    basePath: "/for-sale",
    icon: <SearchIcon />,
    children: [
      {
        label: "All",
        slug: "everything-equine",
      },
      {
        label: "Horses",
        slug: "horses",
      },
      {
        label: "Transport & Vehicles",
        icon: <LocalShippingIcon />,
        slug: "transport-vehicles",
      },
      {
        label: "Saddlery, Tack & Apparel",
        slug: "saddlery-tack-apparel",
      },
      {
        label: "Equine Property",
        slug: "equine-property",
      },
      {
        label: "Stallions at Stud",
        icon: (
          <Image
            src={TubesIcon}
            alt="Stallions at Stud"
            width={20}
            height={20}
          />
        ),
        slug: "stallions-at-stud",
      },
      {
        label: "Products & Equipment",
        slug: "products-equipment",
      },
      {
        label: "Services",
        slug: "services",
      },
    ].map((o) => ({
      ...o,
      icon: MENU_ITEM_ICONS[o.slug],
      href: `/for-sale/${o.slug}`,
    })),
  },
  {
    label: "Sell",
    href: "/sell",
    icon: <HandshakeOutlinedIcon />,
    children: [
      {
        label: "Create Ad",
        href: "/sell",
        icon: <AddIcon />,
      },
      {
        label: "Manage Ads",
        href: "/sell/dashboard",
        icon: <EditIcon />,
      },
    ],
  },
  {
    label: "Hub",
    href: "/hub",
    icon: <GroupsOutlinedIcon />,
    children: [
      {
        label: "Horsezone Hub",
        href: "/hub",
        icon: <HomeIcon />,
      },
      {
        label: "Guides",
        href: "/hub/guides",
        icon: <MenuBookIcon />,
      },
      {
        label: "Off the Track",
        href: "/hub/off-the-track",
        fullRefresh: true,
        icon: <JockeyIcon size="large" />,
      },
    ],
  },
  {
    label: "Insurance",
    href: "/insurance",
    icon: <HealthAndSafetyOutlinedIcon />,
  },
  {
    label: "Buyer Guides",
    href: "/buyer-guides",
    icon: <LocalLibraryOutlinedIcon />,
  },
];

const ACTIVE_MENU_BACKGROUND_COLOR = "grey.100";

function FindSelectedMenuPath(currentPath: string): MenuItem[] {
  function findPath(items: MenuItem[]): MenuItem[] {
    const matchingItems = items.filter((item) =>
      currentPath.startsWith(item.basePath ?? item.href),
    );
    const sortedMatchingItems = matchingItems.sort(
      (a, b) => b.href.length - a.href.length,
    );
    const bestMatch = sortedMatchingItems[0];
    const childMatches = bestMatch?.children
      ? findPath(bestMatch.children)
      : [];
    return bestMatch ? [bestMatch, ...childMatches] : [];
  }
  return findPath(MENU_ITEMS);
}

function useScrollIntoViewRef() {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({
        block: "nearest",
        inline: "center",
        behavior: "smooth",
      });
    }
  }, [ref]);

  return ref;
}

export function GlobalMenu() {
  const selectedNavItemRef = useScrollIntoViewRef();
  const selectedSubNavItemRef = useScrollIntoViewRef();
  const { isFullScreen, setIsFullScreen } = useDeviceNavigation({
    closeOnNavigation: true,
  });
  const pathName = usePathname();
  const selectedMenuPath = FindSelectedMenuPath(pathName || "");
  const { isCollapsed, registerCondition } = useTopNavHeadroom();
  const subNavNeedsSpace =
    selectedMenuPath.some((x) => x.children && x.children.length > 0) &&
    !isFullScreen;

  useEffect(() => {
    registerCondition({
      proposition: !isFullScreen,
      key: "isMenuOpen",
    });
  }, [isFullScreen, registerCondition]);

  return (
    <Box
      component="nav"
      sx={{
        position: "sticky",
        top: 0,
        transform:
          isCollapsed && !isFullScreen
            ? `translateY(calc(-100% - ${subNavNeedsSpace ? TOP_NAV_SUB_MENU_HEIGHT : 0}px + 8px))`
            : "translateY(0)",
        transition:
          "transform 0.3s ease-in-out, margin-bottom 0.3s ease-in-out",
        width: "100%",
        display: "grid",
        gridTemplateColumns: `min-content auto 1fr`,
        gridTemplateRows: "min-content auto",
        backgroundColor: "white",
        zIndex: isFullScreen ? Z_INDEX_MENUS : Z_INDEX_BASE,
        marginBottom: subNavNeedsSpace
          ? TOP_NAV_SUB_MENU_HEIGHT + "px"
          : undefined,
        boxShadow: "0 8px 6px -6px rgba(0, 0, 0, 0.3)",
        maxHeight: "100vh",
        ul: {
          listStyle: "none",
          whiteSpace: "nowrap",
        },
        ...(isFullScreen
          ? {
              position: "fixed",
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              justifyContent: "stretch",
              bottom: 0,
            }
          : {}),
      }}
    >
      <Box
        component={Link}
        href="/"
        sx={(theme) => ({
          gridRow: 1,
          gridColumn: 1,
          padding: theme.spacing(1, 2),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
          marginLeft: `calc((100vw - ${PAGE_MAX_WIDTH}px) / 2)`,
          [theme.breakpoints.down(PAGE_MAX_WIDTH)]: {
            marginLeft: 0,
          },
          img: {
            height: "auto",
            display: "block",
          },
          [theme.breakpoints.down(BREAKPOINT_NAV_DESKTOP_MIN)]: {
            gap: "2px",
            padding: 1,
            img: {
              width: TOP_NAV_LOGO_WIDTH_MOBILE,
            },
          },
        })}
      >
        <Image
          src="/logos/header-desktop-spaced.jpg"
          width={TOP_NAV_LOGO_WIDTH_DESKTOP}
          alt={META_BRAND_NAME}
          height="29"
          priority
          unoptimized
        />
        <Typography
          variant="caption"
          sx={(theme) => ({
            "&&": {
              color: "grey.700",
              fontSize: "15px",
              lineHeight: "15px",
              fontWeight: "100 !important",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              [theme.breakpoints.down(BREAKPOINT_NAV_DESKTOP_MIN)]: {
                fontSize: "12px",
                lineHeight: "12px",
              },
            },
          })}
        >
          {META_AUSTRALIAS_NUMBER_1}
        </Typography>
      </Box>

      <Box
        component="ul"
        sx={(theme) => ({
          gridRow: 1,
          gridColumn: 2,
          display: "flex",
          alignItems: "center",
          overflow: "auto",
          ...HIDE_SCROLLBAR_STYLE,
          ...(isFullScreen
            ? {
                flexDirection: "column",
                alignItems: "start",
                order: 2,
              }
            : {}),
          [theme.breakpoints.down(BREAKPOINT_NAV_WRAPPED_MIN)]: {
            gridRow: 2,
            gridColumn: `1 / -1`,
            width: "100%",
          },
        })}
      >
        {MENU_ITEMS.map((item) => {
          const isSelected = selectedMenuPath.includes(item);
          return (
            <Box
              key={item.label}
              component="li"
              sx={(theme) => ({
                display: "flex",
                alignSelf: "stretch",
                backgroundColor: "white",
                ...(isFullScreen
                  ? {
                      height: "auto",
                      width: "100%",
                      flexDirection: "column",
                      "&&& .sub-menu": {
                        display: "block",
                      },
                      "&:last-child": {
                        marginBottom: 4,
                        borderBottom: `1px solid`,
                        borderBottomColor: "divider",
                      },
                    }
                  : {
                      [theme.breakpoints.down(BREAKPOINT_NAV_WRAPPED_MIN)]: {
                        ":nth-child(n+2)": {
                          ".MuiSvgIcon-root, img": {
                            display: "none",
                          },
                        },
                      },
                    }),
                ".sub-menu": {
                  display: isSelected ? "flex" : "none",
                },
              })}
            >
              <GlobalNavMenu
                menu={
                  item.children?.map((child) => ({
                    type: "item",
                    label: child.label,
                    href: child.href,
                    fullRefresh: child.fullRefresh,
                    icon: child.icon,
                  })) || []
                }
                disableMenu={isSelected}
                disableMenuOnMobile
              >
                <Box
                  component={Link}
                  href={item.href}
                  ref={isSelected ? selectedNavItemRef : null}
                  sx={(theme) => ({
                    height: "100%",
                    backgroundColor: isSelected
                      ? ACTIVE_MENU_BACKGROUND_COLOR
                      : "transparent",
                    padding: theme.spacing(2, 4),
                    [theme.breakpoints.down(BREAKPOINT_NAV_RELAXED_MIN)]: {
                      padding: 2,
                    },
                    ...theme.typography.body1,
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    ...(isFullScreen
                      ? {
                          fontSize: theme.typography.h5.fontSize,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          gap: 2,
                        }
                      : {
                          gap: 1,
                        }),
                  })}
                >
                  {item.icon}
                  {item.label}
                  {item.children && !isSelected && (
                    <ExpandMoreIcon
                      sx={(theme) => ({
                        [theme.breakpoints.down(BREAKPOINT_NAV_WRAPPED_MIN)]: {
                          display: "none",
                        },
                      })}
                    />
                  )}
                </Box>
              </GlobalNavMenu>

              <Box
                sx={(theme) => ({
                  display: "block",
                  height: TOP_NAV_SUB_MENU_HEIGHT,
                  width: theme.spacing(1),
                  position: "absolute",
                  right: 0,
                  background:
                    "linear-gradient(90deg, rgba(200, 200, 200, 0), rgba(200, 200, 200,0.3))",
                  top: TOP_NAV_MENU_HEIGHT_LARGE,
                  [theme.breakpoints.down(BREAKPOINT_NAV_DESKTOP_MIN)]: {
                    top: TOP_NAV_MENU_HEIGHT_SMALL,
                  },
                  [theme.breakpoints.down(BREAKPOINT_NAV_WRAPPED_MIN)]: {
                    top: TOP_NAV_MENU_HEIGHT_SMALL * 2,
                  },
                  ...(isFullScreen ? { display: "none" } : {}),
                })}
              ></Box>

              {item.children && (
                <Box
                  component="ul"
                  className="sub-menu"
                  sx={(theme) => ({
                    display: "flex",
                    maxWidth: "100%",
                    alignItems: "flex-end",
                    overflowX: "auto",
                    overflowY: "hidden",
                    boxShadow: "0 8px 6px -6px rgba(0, 0, 0, 0.3)",
                    ...(!isFullScreen
                      ? {
                          position: "absolute",
                          top: TOP_NAV_MENU_HEIGHT_LARGE,
                          left: 0,
                          right: 0,
                          paddingLeft: `calc((100% - ${PAGE_MAX_WIDTH}px) / 2)`,
                          backgroundColor: ACTIVE_MENU_BACKGROUND_COLOR,
                          [theme.breakpoints.down(BREAKPOINT_NAV_DESKTOP_MIN)]:
                            {
                              top: TOP_NAV_MENU_HEIGHT_SMALL,
                              paddingLeft: 2,
                            },
                          [theme.breakpoints.down(BREAKPOINT_NAV_WRAPPED_MIN)]:
                            {
                              top: TOP_NAV_MENU_HEIGHT_SMALL * 2,
                              paddingLeft: 0,
                            },
                          height: TOP_NAV_SUB_MENU_HEIGHT,
                          [theme.breakpoints.up("sm")]: {
                            ...HIDE_SCROLLBAR_STYLE,
                          },
                        }
                      : {
                          height: "auto",
                          flexDirection: "column",
                          gap: 2,
                          bottom: 0,
                          backgroundColor: "white",
                        }),
                  })}
                >
                  {item.children.map((child) => {
                    const isSelectedChild = selectedMenuPath.includes(child);
                    return (
                      <Box
                        key={child.label}
                        component="li"
                        ref={isSelectedChild ? selectedSubNavItemRef : null}
                      >
                        <Typography
                          component={child.fullRefresh ? "a" : Link}
                          href={child.href}
                          sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            ...(isSelectedChild
                              ? {
                                  backgroundColor: "grey.200",
                                  fontWeight: "bold",
                                  boxShadow: `inset 0 -4px 0 0 ${BRAND_COLOR}`,
                                }
                              : {}),
                            padding: theme.spacing(2, 3),
                            gap: 2,
                            [theme.breakpoints.down(
                              BREAKPOINT_NAV_WRAPPED_MIN,
                            )]: {
                              padding: 2,
                              gap: 1,
                              ...(isFullScreen
                                ? {
                                    gap: 2,
                                  }
                                : {}),
                            },
                            "@media (hover: hover)": {
                              "&:hover": {
                                backgroundColor: "grey.200",
                              },
                            },
                            "img, .MuiSvgIcon-root": {
                              width: "24px",
                              height: "24px",
                            },
                          })}
                        >
                          {child.icon}
                          {child.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <Box
        component="ul"
        sx={{
          gridRow: 1,
          gridColumn: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          minHeight: TOP_NAV_MENU_HEIGHT_SMALL,
          overflow: "hidden",
          ...(isFullScreen
            ? {
                order: 1,
                alignSelf: "flex-end",
                width: "100%",
              }
            : {}),
        }}
      >
        <Box
          component="li"
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            height: "100%",
            gap: 1,
            marginRight: `calc((100vw - ${PAGE_MAX_WIDTH}px) / 2)`,
            [theme.breakpoints.down(PAGE_MAX_WIDTH)]: {
              marginRight: 0,
            },
            [theme.breakpoints.down(BREAKPOINT_NAV_MOBILE_MIN)]: {
              padding: 1,
              gap: 1,
              width: "100%",
              justifyContent: "end",
            },
          })}
        >
          <Box
            sx={(theme) => ({
              height: "100%",
              display: "flex",
              alignItems: "stretch",
              [theme.breakpoints.down(BREAKPOINT_NAV_MOBILE_MIN)]: {
                maxHeight: "40px",
                backgroundColor: "grey.100",
                borderRadius: "100px",
                alignItems: "center",
              },
            })}
          >
            <GlobalMemberMenu isMenuOpen={isFullScreen} />
          </Box>
          <IconButton
            onClick={() => setIsFullScreen(!isFullScreen)}
            sx={(theme) => ({
              color: "black",
              backgroundColor: "grey.100",
              ...(isFullScreen
                ? {
                    position: "absolute",
                    top: theme.spacing(1),
                    right: theme.spacing(1),
                  }
                : {
                    [theme.breakpoints.up(BREAKPOINT_NAV_MOBILE_MIN)]: {
                      display: "none",
                    },
                  }),
            })}
          >
            {isFullScreen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
